import { assessmentScore, assessmentSubjectKey } from './assessmentScoreSummary';
import { assessmentDevelopmentLevel } from '../../shared/developmentLevels';

const mean = values => values.length
  ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

const day = value => String(value || '').slice(0, 10);

/**
 * The selected class, assessment and date filters have already been applied to
 * rows by ClassPulseDashboard. This read-only view never queries another class.
 * Subject average comes from buildClassPulse (equal weight per student), while
 * exam average is the arithmetic mean of that exam's valid scored results.
 */
export function buildClassPulseSubjectDetail(subject, rows = [], students = []) {
  if (!subject || subject.key === 'unassigned') return null;
  const exams = new Map();
  for (const row of rows) {
    if (assessmentSubjectKey(row) !== subject.key) continue;
    const examId = String(row.exam_id ?? `date:${day(row.exam_date)}:${row.exam_title || ''}`);
    if (!exams.has(examId)) {
      exams.set(examId, {
        id: examId, date: day(row.exam_date), title: String(row.exam_title || 'Değerlendirme'),
        scores: [], byStudent: new Map(), scoringMode: row.scoring_mode || 'manual',
      });
    }
    const exam = exams.get(examId);
    const studentId = String(row.student_id ?? '');
    // Match the existing class metric duplicate policy (first row per exam+student).
    if (!studentId || exam.byStudent.has(studentId)) continue;
    const score = assessmentScore(row);
    const developmentLevel = assessmentDevelopmentLevel(row);
    const status = String(row.result_status || '').toLowerCase() === 'absent'
      ? 'absent' : score === null && !developmentLevel ? 'pending' : 'completed';
    exam.byStudent.set(studentId, { score, status, developmentLevel });
    if (score !== null) exam.scores.push(score);
  }
  const studentList = students.map(item => ({
    id: String(item.id), name: item.name,
    avatarId: item.avatarId, starterEmoji: item.starterEmoji,
  }));
  const items = [...exams.values()].map(exam => {
    const participants = studentList.map(student => ({
      ...student,
      ...(exam.byStudent.get(student.id) || { score: null, status: 'pending' }),
    }));
    const recorded = [...exam.byStudent.values()];
    return {
      id: exam.id, title: exam.title, date: exam.date,
      average: mean(exam.scores), scoredCount: exam.scores.length,
      scoringMode: exam.scoringMode,
      evaluatedCount: recorded.filter(row => row.status === 'completed').length,
      distribution: Object.fromEntries(['level_1', 'level_2', 'level_3', 'level_4'].map(level => [level,
        recorded.filter(row => row.developmentLevel === level).length])),
      absentCount: recorded.filter(row => row.status === 'absent').length,
      pendingCount: recorded.filter(row => row.status === 'pending').length +
        participants.filter(student => !exam.byStudent.has(student.id)).length,
      students: participants,
    };
  }).sort((a, b) => b.date.localeCompare(a.date) || a.title.localeCompare(b.title, 'tr-TR'));
  const assessedIds = new Set(items.flatMap(exam => exam.students.filter(s => s.score !== null && s.score !== undefined).map(s => s.id)));
  return {
    key: subject.key, label: subject.label,
    average: subject.average,
    totalStudents: studentList.length,
    scoredStudents: assessedIds.size,
    examCount: items.length,
    exams: items,
  };
}

/** Plot historical exam averages; only distinct dated exams with scored results qualify. */
export const subjectChartPoints = (exams = []) => exams
  .filter(exam => /^\d{4}-\d{2}-\d{2}$/.test(exam.date) && Number.isFinite(exam.average))
  .sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
